import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import PetsOutlinedIcon from "@mui/icons-material/PetsOutlined";
import SquareFootOutlinedIcon from "@mui/icons-material/SquareFootOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import { Box, Button, Chip, Collapse, FormControl, IconButton, MenuItem, Select, Stack, TableCell, TableRow, Tooltip, Typography, useMediaQuery } from "@mui/material";

import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectService, setQuote } from "../../../redux/features/jobCreateSlice";
import { BaseModalComponent } from "../../common/BaseModalComponent";
import { useQuoteDepositAvailable } from "../../jobs2/JobCreate/hooks/useQuoteDepositAvailable";
import { colors, alphaVar } from "../../ui/colors";
import { usePatchQuoteStatus } from "../hooks/usePatchQuoteStatus";
import { quoteModern } from "../quote-detail/summary/style/style";
import { QuoteView } from "../quote-detail/view/QuoteView";
import { DepositCell } from "./DepositCell";
import { QuoteRowActionsMenu } from "./QuoteRowActionsMenu";

function getClientName(client) {
  if (client?.clientCompanyName) {
    return client.clientCompanyName;
  }

  if (client?.firstName || client?.lastName) {
    return `${client.firstName ?? ""} ${client.lastName ?? ""}`.trim();
  }

  return "N/A";
}

function getLocation(coverageArea) {
  if (!coverageArea) return "N/A";

  if (coverageArea.address) {
    return coverageArea.address;
  }

  const parts = [coverageArea.city, coverageArea.state, coverageArea.country].filter(Boolean);

  return parts.length > 0 ? parts.join(", ") : "N/A";
}

function humanizeToken(value) {
  if (value == null || value === "") return "";

  return String(value).replace(/_/g, " ");
}

function formatScheduleHint(eventPreferences) {
  const config = eventPreferences?.eventConfig;

  const duration = config?.duration != null ? `${config.duration} min` : null;

  const mode = config?.mode != null ? humanizeToken(config.mode) : null;

  const rawDate = eventPreferences?.fixedTimes?.[0] || eventPreferences?.eventTime;

  const scheduledDate = rawDate ? dayjs(rawDate).format("MMM D, YYYY h:mm A") : null;

  const values = [scheduledDate, duration, mode].filter(Boolean);

  return values.length > 0 ? values.join(" · ") : null;
}

function isCancelledJobStatus(status) {
  return String(status || "").toLowerCase() === "cancelled";
}

function resolveJobQuoteServiceLineUid(job) {
  if (!job) return null;

  const quoteServiceUid = job.quoteServiceUid;

  if (typeof quoteServiceUid === "string" && quoteServiceUid.trim() !== "") {
    return quoteServiceUid;
  }

  const depositServiceUid = job.depositReservedQuoteServiceUid;

  if (typeof depositServiceUid === "string" && depositServiceUid.trim() !== "") {
    return depositServiceUid;
  }

  return null;
}

function buildLockingJobByQuoteServiceUid(jobs) {
  const map = new Map();

  if (!Array.isArray(jobs)) {
    return map;
  }

  for (const job of jobs) {
    if (!job || isCancelledJobStatus(job.status)) {
      continue;
    }

    const quoteServiceUid = resolveJobQuoteServiceLineUid(job);

    if (!quoteServiceUid || map.has(quoteServiceUid)) {
      continue;
    }

    map.set(quoteServiceUid, job);
  }

  return map;
}

const clean = (value) => String(value ?? "").trim();

const formatText = (value) => {
  const normalized = clean(value);

  if (!normalized) return "";

  const values = {
    by_weekly: "Bi-weekly",
    by_monthly: "Bi-monthly",
    weekly: "Weekly",
    monthly: "Monthly",
    unique: "Custom price",
    one_time: "One-time",
    per_floor: "Per floor",
    per_area: "Per area",
    per_sqft: "Per sqft",
  };

  const key = normalized.toLowerCase();

  if (values[key]) {
    return values[key];
  }

  return normalized.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
};

const formatMoney = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "—";
  }

  return number.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

const getAreaQuantity = (area) => {
  if (area?.quantity != null) {
    const quantity = Number(area.quantity);

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
  }

  const squareFootages = Array.isArray(area?.squareFootages) ? area.squareFootages : [];

  return squareFootages.reduce((total, item) => total + (Number(item?.quantity) || 0), 0);
};

const getAreaName = (area) => {
  return clean(area?.area?.name || area?.name || area?.areaName);
};

const getActiveAreas = (areas) => {
  if (!Array.isArray(areas)) {
    return [];
  }

  return areas.filter((area) => getAreaQuantity(area) > 0);
};

const resolveAreas = (scope, service) => {
  const combinedAreas = Array.isArray(scope?.areasByScopesOfWork)
    ? scope.areasByScopesOfWork
    : Array.isArray(service?.areasByScopesOfWork)
      ? service.areasByScopesOfWork
      : [];

  if (combinedAreas.length > 0) {
    const activeAreas = getActiveAreas(combinedAreas);

    return {
      regularAreas: activeAreas.filter((area) => area?.otherArea !== true),
      otherAreas: activeAreas.filter((area) => area?.otherArea === true),
    };
  }

  const sourceAreas = getActiveAreas(Array.isArray(scope?.areas) ? scope.areas : Array.isArray(service?.areas) ? service.areas : []);

  const regularAreas = sourceAreas.filter((area) => area?.otherArea !== true);

  const otherAreasFromSource = sourceAreas.filter((area) => area?.otherArea === true);

  const legacyOtherAreas = getActiveAreas(
    Array.isArray(scope?.otherAreas) ? scope.otherAreas : Array.isArray(service?.otherAreas) ? service.otherAreas : [],
  );

  return {
    regularAreas,
    otherAreas: otherAreasFromSource.length > 0 ? otherAreasFromSource : legacyOtherAreas,
  };
};

const getAdditionalObjects = (scope, service) => {
  if (Array.isArray(scope?.additionalObjects)) {
    return scope.additionalObjects;
  }

  if (Array.isArray(scope?.additionalObjectsByScopesOfWork)) {
    return scope.additionalObjectsByScopesOfWork;
  }

  if (Array.isArray(service?.additionalObjects)) {
    return service.additionalObjects;
  }

  return [];
};

const getSpecialRequests = (scope, service) => {
  if (Array.isArray(scope?.specialRequests)) {
    return scope.specialRequests;
  }

  if (Array.isArray(scope?.specialRequestsByScopesOfWork)) {
    return scope.specialRequestsByScopesOfWork;
  }

  if (Array.isArray(service?.specialRequests)) {
    return service.specialRequests;
  }

  return [];
};

const getAdditionalObjectName = (item) => {
  return clean(item?.name || item?.additionalObject?.name || item?.object?.name);
};

const getSpecialRequestName = (item) => {
  return clean(item?.name || item?.specialRequest?.name);
};

const getServiceChips = (scope, service) => {
  const chips = [];

  const category = formatText(scope?.category?.type || service?.category?.type);

  const typeOfPlace = clean(scope?.typeOfPlace?.name || scope?.typeOfPlace?.typeOfPlace?.name || service?.typeOfPlace?.name);

  const frequency = formatText(scope?.frequencyType || service?.frequencyType);

  const squareFootage = clean(scope?.squareFootage || service?.squareFt || service?.squareFootage);

  if (category) {
    chips.push({
      label: category,
      tooltip: "Service category",
      icon: <HomeWorkOutlinedIcon sx={{ fontSize: 15 }} />,
    });
  }

  if (typeOfPlace) {
    chips.push({
      label: typeOfPlace,
      tooltip: "Type of place",
      icon: <HomeWorkOutlinedIcon sx={{ fontSize: 15 }} />,
    });
  }

  if (frequency) {
    chips.push({
      label: frequency,
      tooltip: "Service frequency",
      icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 15 }} />,
    });
  }

  if (squareFootage) {
    chips.push({
      label: `${squareFootage} sqft`,
      tooltip: "Total property area",
      icon: <SquareFootOutlinedIcon sx={{ fontSize: 15 }} />,
    });
  }

  return chips;
};

const getServiceStats = ({ scope, service, regularAreas, otherAreas, scheduleHint }) => {
  const stats = [];

  const additionalObjects = getAdditionalObjects(scope, service);

  const specialRequests = getSpecialRequests(scope, service);

  const regularAreaQuantity = regularAreas.reduce((total, area) => total + getAreaQuantity(area), 0);

  const otherAreaQuantity = otherAreas.reduce((total, area) => total + getAreaQuantity(area), 0);

  const totalAreaQuantity = regularAreaQuantity + otherAreaQuantity;

  const regularAreaNames = regularAreas
    .map((area) => {
      const name = getAreaName(area);
      const quantity = getAreaQuantity(area);

      return name ? `${name} × ${quantity}` : "";
    })
    .filter(Boolean)
    .join(", ");

  const otherAreaNames = otherAreas
    .map((area) => {
      const name = getAreaName(area);
      const quantity = getAreaQuantity(area);

      return name ? `${name} × ${quantity}` : "";
    })
    .filter(Boolean)
    .join(", ");

  const additionalObjectNames = additionalObjects.map(getAdditionalObjectName).filter(Boolean).join(", ");

  const specialRequestNames = specialRequests.map(getSpecialRequestName).filter(Boolean).join(", ");

  if (regularAreaQuantity > 0) {
    stats.push({
      label: "Areas included",
      value: `${regularAreaQuantity} ${regularAreaQuantity === 1 ? "area" : "areas"}`,
      tooltip: regularAreaNames,
      icon: <GridViewOutlinedIcon sx={{ fontSize: 18 }} />,
    });
  }

  if (otherAreaQuantity > 0) {
    stats.push({
      label: "Other areas",
      value: `${otherAreaQuantity} ${otherAreaQuantity === 1 ? "area" : "areas"}`,
      tooltip: otherAreaNames,
      icon: <HomeWorkOutlinedIcon sx={{ fontSize: 18 }} />,
    });
  }

  if (totalAreaQuantity > 0) {
    stats.push({
      label: "Total areas",
      value: `${totalAreaQuantity} ${totalAreaQuantity === 1 ? "area" : "areas"}`,
      tooltip: "Combined quantity of included and other areas",
      icon: <GridViewOutlinedIcon sx={{ fontSize: 18 }} />,
    });
  }

  if (additionalObjects.length > 0) {
    stats.push({
      label: "Additional objects",
      value: `${additionalObjects.length} ${additionalObjects.length === 1 ? "object" : "objects"}`,
      tooltip: additionalObjectNames,
      icon: <AddCircleOutlineIcon sx={{ fontSize: 18 }} />,
    });
  }

  if (specialRequests.length > 0) {
    stats.push({
      label: "Special requests",
      value: `${specialRequests.length} ${specialRequests.length === 1 ? "request" : "requests"}`,
      tooltip: specialRequestNames,
      icon: <PetsOutlinedIcon sx={{ fontSize: 18 }} />,
    });
  }

  return stats;
};

// ─── Status ─

const STATUS_MAP = {
  approved: {
    label: "Approved",
    backgroundColor: colors.success[100],
    color: colors.success[800],
  },
  declined: {
    label: "Declined",
    backgroundColor: colors.danger[100],
    color: colors.danger[800],
  },
  revision: {
    label: "Revision",
    backgroundColor: colors.warning[100],
    color: colors.warning[800],
  },
  paid: {
    label: "Paid",
    backgroundColor: colors.info[100],
    color: colors.info[800],
  },
};

function getStatusChipProps(status) {
  if (!status) {
    return {
      label: "Pending",
      backgroundColor: colors.warning[100],
      color: colors.warning[800],
    };
  }

  return (
    STATUS_MAP[status.toLowerCase()] ?? {
      label: status,
      backgroundColor: colors.gray[200],
      color: colors.gray[700],
    }
  );
}

function StatusSelect({ quoteId, status, size = "small" }) {
  const { mutate, isPending } = usePatchQuoteStatus();

  const [displayStatus, setDisplayStatus] = useState(status?.toLowerCase() || "pending");

  useEffect(() => {
    if (status) {
      setDisplayStatus(status.toLowerCase());
    }
  }, [status]);

  const { backgroundColor, color } = getStatusChipProps(displayStatus);

  const handleChange = (event) => {
    event.stopPropagation();

    const newStatus = event.target.value;

    if (newStatus === displayStatus) {
      return;
    }

    const previousStatus = displayStatus;

    setDisplayStatus(newStatus);

    mutate(
      {
        quoteId,
        data: {
          status: newStatus,
        },
      },
      {
        onError: () => {
          setDisplayStatus(previousStatus);
        },
      },
    );
  };

  return (
    <FormControl
      size="small"
      onClick={(event) => event.stopPropagation()}
      sx={{
        m: 0,
        p: 0,
      }}
    >
      <Select
        value={displayStatus}
        onChange={handleChange}
        disabled={isPending}
        variant="standard"
        disableUnderline
        sx={{
          fontSize: size === "small" ? "0.65rem" : "0.75rem",
          height: 24,
          minWidth: 100,
          backgroundColor: alphaVar(backgroundColor, 0.2),
          color,
          borderRadius: "16px",
          border: `1px solid ${alphaVar(color, 0.3)}`,
          pl: 1,
          transition: "all 0.2s ease",

          "& .MuiSelect-select": {
            padding: "2px 0 2px 4px",
            display: "flex",
            alignItems: "center",
            textTransform: "capitalize",
          },

          "& .MuiSvgIcon-root": {
            fontSize: "1.1rem",
            color,
            right: 4,
          },

          "&:hover": {
            backgroundColor: alphaVar(backgroundColor, 0.3),
            borderColor: color,
          },
        }}
        MenuProps={{
          onClick: (event) => event.stopPropagation(),

          sx: {
            zIndex: 99999,
          },

          PaperProps: {
            sx: {
              mt: 0.5,

              "& .MuiMenuItem-root": {
                fontSize: "0.75rem",
                textTransform: "capitalize",
              },
            },
          },
        }}
      >
        {Object.entries(STATUS_MAP).map(([key, value]) => (
          <MenuItem key={key} value={key}>
            {value.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function AdvancedServiceCard({
  service,
  isSelected,
  onSelect,
  depositReservedQuoteServiceUid,
  depositAvailable,
  depositRecognizedPaid,
  quoteDepositAmount,
  lockingJob,
}) {
  const isLockedByJob = Boolean(lockingJob?.id);

  const scope = service?.scope ?? service?.scopeOfWork ?? service?.sectorWorkflow ?? service;

  const originalServiceName = clean(scope?.typeOfService?.name) || clean(service?.service) || clean(scope?.name) || clean(service?.name) || "Service";

  const serviceNameOverride = clean(service?.serviceNameOverride) || clean(service?.alias) || clean(scope?.serviceNameOverride);

  const hasServiceNameOverride = Boolean(serviceNameOverride && serviceNameOverride.toLowerCase() !== originalServiceName.toLowerCase());

  const serviceName = serviceNameOverride || originalServiceName;

  const { regularAreas, otherAreas } = resolveAreas(scope, service);

  const scheduleHint = formatScheduleHint(service?.eventPreferences);

  const chips = getServiceChips(scope, service);

  const stats = getServiceStats({
    scope,
    service,
    regularAreas,
    otherAreas,
    scheduleHint,
  });

  const total = Number(service?.total ?? scope?.total ?? scope?.costOverride?.value ?? 0);
  const isDepositHolder = Boolean(depositReservedQuoteServiceUid && service?.serviceUid && depositReservedQuoteServiceUid === service.serviceUid);

  const depositLockedElsewhere = Boolean(
    depositRecognizedPaid &&
    (depositAvailable ?? 0) <= 0 &&
    depositReservedQuoteServiceUid &&
    service?.serviceUid &&
    depositReservedQuoteServiceUid !== service.serviceUid,
  );

  const card = (
    <Box
      onClick={() => {
        if (isLockedByJob) return;
        onSelect();
      }}
      sx={{
        width: "100%",
        p: 1.5,
        borderRadius: 2.5,
        bgcolor: isSelected ? alphaVar(quoteModern.colors.orange, 0.035) : "var(--fx-surface-base, #FFFFFF)",
        border: `1px solid ${isSelected ? quoteModern.colors.orange : quoteModern.colors.line}`,
        boxShadow: quoteModern.colors.shadow,
        cursor: isLockedByJob ? "not-allowed" : "pointer",
        opacity: isLockedByJob ? 0.62 : 1,
        transition: "all 150ms ease",

        ...(!isLockedByJob
          ? {
              "&:hover": {
                borderColor: alphaVar(quoteModern.colors.orange, 0.45),
                transform: "translateY(-1px)",
                boxShadow: `0 6px 20px ${alphaVar(quoteModern.colors.orange, 0.08)}`,
              },
            }
          : {}),
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.75,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1,
                py: 0.45,
                borderRadius: 999,
                bgcolor: quoteModern.colors.orangeSoft,
                color: quoteModern.colors.orange,
              }}
            >
              <DescriptionIcon sx={{ fontSize: 14 }} />

              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  lineHeight: 1,
                }}
              >
                Advanced service
              </Typography>
            </Box>

            {isSelected && (
              <Chip
                label="Selected"
                size="small"
                sx={{
                  height: 20,
                  bgcolor: quoteModern.colors.orange,
                  color: "#FFFFFF",
                  fontSize: 10,
                  fontWeight: 800,
                }}
              />
            )}

            {isLockedByJob && (
              <Chip
                label={`Job #${lockingJob.id}`}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                  borderColor: quoteModern.colors.line,
                  color: quoteModern.colors.textSecondary,
                  fontSize: 10,
                  fontWeight: 800,
                }}
              />
            )}
          </Box>

          <Typography
            sx={{
              mt: 1,
              fontSize: 17,
              fontWeight: 900,
              color: quoteModern.colors.textPrimary,
              lineHeight: 1.2,
              wordBreak: "break-word",
            }}
          >
            {serviceName}
          </Typography>

          {hasServiceNameOverride && (
            <Box
              sx={{
                mt: 0.65,
                display: "inline-flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.45,
                px: 1,
                py: 0.45,
                borderRadius: 999,
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                border: `1px solid ${quoteModern.colors.line}`,
                maxWidth: "100%",
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: quoteModern.colors.textSecondary,
                }}
              >
                Original service:
              </Typography>

              <Typography
                component="span"
                title={originalServiceName}
                sx={{
                  minWidth: 0,
                  fontSize: 11,
                  fontWeight: 600,
                  color: quoteModern.colors.textPrimary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {originalServiceName}
              </Typography>
            </Box>
          )}

          {chips.length > 0 && (
            <Box
              sx={{
                mt: 1.25,
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
              }}
            >
              {chips.map((chip) => (
                <Tooltip key={`${chip.tooltip}-${chip.label}`} title={chip.tooltip} arrow placement="top">
                  <Box
                    sx={{
                      px: 1,
                      py: 0.6,
                      borderRadius: 999,
                      bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                      border: `1px solid ${quoteModern.colors.line}`,
                      color: quoteModern.colors.textPrimary,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.6,
                      fontSize: 12,
                      fontWeight: 800,
                      lineHeight: 1,

                      "& svg": {
                        color: quoteModern.colors.orange,
                      },
                    }}
                  >
                    {chip.icon}
                    {chip.label}
                  </Box>
                </Tooltip>
              ))}
            </Box>
          )}
        </Box>

        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 18,
            lineHeight: 1.2,
            color: quoteModern.colors.orange,
          }}
        >
          {formatMoney(total)}
        </Typography>
      </Box>
      {stats.length > 0 && (
        <Box
          sx={{
            mt: 1.5,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
            gap: 1,
          }}
        >
          {stats.map((item) => (
            <Tooltip key={item.label} title={item.tooltip} arrow placement="top">
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                  border: `1px solid ${quoteModern.colors.line}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 0,
                  transition: "all 150ms ease",

                  "&:hover": {
                    bgcolor: alphaVar(quoteModern.colors.orange, 0.05),
                    borderColor: alphaVar(quoteModern.colors.orange, 0.24),
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Box sx={quoteModern.iconBoxSx}>{item.icon}</Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={quoteModern.labelSx}>{item.label}</Typography>

                  <Typography
                    title={item.value}
                    sx={{
                      mt: 0.35,
                      fontSize: 12.5,
                      fontWeight: 800,
                      color: quoteModern.colors.textPrimary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}

      {(isDepositHolder || depositLockedElsewhere || isLockedByJob) && (
        <Box
          sx={{
            mt: 1.25,
            pt: 1.15,
            borderTop: `1px solid ${quoteModern.colors.line}`,
            display: "flex",
            gap: 0.75,
            flexWrap: "wrap",
          }}
        >
          {isDepositHolder && <Chip label="Deposit applied" size="small" />}

          {depositLockedElsewhere && <Chip label="Deposit reserved elsewhere" size="small" />}

          {isLockedByJob && <Chip label="Already linked to a job" size="small" />}
        </Box>
      )}
    </Box>
  );

  if (isLockedByJob) {
    return (
      <Tooltip title={`Job #${lockingJob.id} is already linked to this quote service.`} arrow>
        <span style={{ display: "block" }}>{card}</span>
      </Tooltip>
    );
  }

  return card;
}

function BasicServiceCard({
  service,
  isSelected,
  onSelect,
  depositReservedQuoteServiceUid,
  depositAvailable,
  depositRecognizedPaid,
  quoteDepositAmount,
  lockingJob,
}) {
  const isLockedByJob = Boolean(lockingJob?.id);

  const scope = service?.scope ?? service?.scopeOfWork ?? service?.sectorWorkflow ?? null;

  const originalServiceName = clean(service?.service) || clean(scope?.name) || clean(service?.name) || "Unnamed Service";

  const serviceNameOverride = clean(service?.serviceNameOverride) || clean(service?.alias) || clean(scope?.serviceNameOverride);

  const hasServiceNameOverride = Boolean(serviceNameOverride && serviceNameOverride.toLowerCase() !== originalServiceName.toLowerCase());

  const serviceName = serviceNameOverride || originalServiceName;

  const price = Number(service?.price ?? scope?.costOverride?.value ?? 0);

  const units = Number(service?.units ?? 0);

  const total = Number(service?.total ?? price * units);

  const measure = clean(service?.measure);
  const notes = clean(service?.notes || scope?.description);

  const isDepositHolder = Boolean(depositReservedQuoteServiceUid && service?.serviceUid && depositReservedQuoteServiceUid === service.serviceUid);

  const depositLockedElsewhere = Boolean(
    depositRecognizedPaid &&
    (depositAvailable ?? 0) <= 0 &&
    depositReservedQuoteServiceUid &&
    service?.serviceUid &&
    depositReservedQuoteServiceUid !== service.serviceUid,
  );

  const depositValue = quoteDepositAmount != null && Number(quoteDepositAmount) > 0 ? formatMoney(quoteDepositAmount) : null;

  const card = (
    <Box
      onClick={() => {
        if (isLockedByJob) return;

        onSelect();
      }}
      sx={{
        mt: 1,
        width: "100%",
        p: 1.5,
        borderRadius: 2.5,
        bgcolor: isSelected ? alphaVar(quoteModern.colors.orange, 0.035) : "var(--fx-surface-base, #FFFFFF)",
        border: `1px solid ${isSelected ? quoteModern.colors.orange : quoteModern.colors.line}`,
        boxShadow: quoteModern.colors.shadow,
        cursor: isLockedByJob ? "not-allowed" : "pointer",
        opacity: isLockedByJob ? 0.62 : 1,
        transition: "all 150ms ease",

        ...(!isLockedByJob
          ? {
              "&:hover": {
                borderColor: alphaVar(quoteModern.colors.orange, 0.45),
                transform: "translateY(-1px)",
                boxShadow: `0 6px 20px ${alphaVar(quoteModern.colors.orange, 0.08)}`,
              },
            }
          : {}),
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.75,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1,
                py: 0.45,
                borderRadius: 999,
                bgcolor: quoteModern.colors.orangeSoft,
                color: quoteModern.colors.orange,
              }}
            >
              <DescriptionIcon sx={{ fontSize: 14 }} />

              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  lineHeight: 1,
                }}
              >
                Basic service
              </Typography>
            </Box>

            {isSelected && (
              <Chip
                label="Selected"
                size="small"
                sx={{
                  height: 20,
                  bgcolor: quoteModern.colors.orange,
                  color: "#FFFFFF",
                  fontSize: 10,
                  fontWeight: 800,
                }}
              />
            )}

            {isLockedByJob && (
              <Chip
                label={`Job #${lockingJob.id}`}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                  borderColor: quoteModern.colors.line,
                  color: quoteModern.colors.textSecondary,
                  fontSize: 10,
                  fontWeight: 800,
                }}
              />
            )}
          </Box>

          <Typography
            sx={{
              mt: 1,
              fontSize: 17,
              fontWeight: 900,
              color: quoteModern.colors.textPrimary,
              lineHeight: 1.2,
              wordBreak: "break-word",
            }}
          >
            {serviceName}
          </Typography>

          {hasServiceNameOverride && (
            <Box
              sx={{
                mt: 0.65,
                display: "inline-flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.45,
                px: 1,
                py: 0.45,
                borderRadius: 999,
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                border: `1px solid ${quoteModern.colors.line}`,
                maxWidth: "100%",
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: quoteModern.colors.textSecondary,
                  flexShrink: 0,
                }}
              >
                Original service:
              </Typography>

              <Typography
                component="span"
                title={originalServiceName}
                sx={{
                  minWidth: 0,
                  fontSize: 11,
                  fontWeight: 600,
                  color: quoteModern.colors.textPrimary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {originalServiceName}
              </Typography>
            </Box>
          )}
        </Box>

        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 18,
            lineHeight: 1.2,
            color: quoteModern.colors.orange,
            flexShrink: 0,
          }}
        >
          {formatMoney(total)}
        </Typography>
      </Box>

      <Box
        sx={{
          mt: 1.5,
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          minWidth: 190,
          maxWidth: "100%",
          px: 1.25,
          py: 0.9,
          borderRadius: 2,
          bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
          border: `1px solid ${quoteModern.colors.line}`,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.75,
            color: quoteModern.colors.orange,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AttachMoneyOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography sx={quoteModern.labelSx}>Price</Typography>

          <Typography
            sx={{
              mt: 0.3,
              fontSize: 12.5,
              fontWeight: 800,
              color: quoteModern.colors.textPrimary,
              lineHeight: 1.25,
              whiteSpace: "nowrap",
            }}
          >
            {formatMoney(price)}
            <Box
              component="span"
              sx={{
                mx: 0.65,
                color: quoteModern.colors.textSecondary,
              }}
            >
              •
            </Box>
            {units} unit
            {units !== 1 ? "s" : ""}
            {measure && (
              <>
                <Box
                  component="span"
                  sx={{
                    mx: 0.65,
                    color: quoteModern.colors.textSecondary,
                  }}
                >
                  •
                </Box>

                {measure}
              </>
            )}
          </Typography>
        </Box>
      </Box>

      {notes && (
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "flex-start",
            gap: 0.75,
            color: quoteModern.colors.textSecondary,
          }}
        >
          <NotesOutlinedIcon
            sx={{
              mt: 0.1,
              fontSize: 16,
              color: quoteModern.colors.orange,
              flexShrink: 0,
            }}
          />

          <Typography
            sx={{
              fontSize: 12.5,
              lineHeight: 1.4,
              wordBreak: "break-word",
            }}
          >
            <Box component="span" sx={{ fontWeight: 800 }}>
              Notes:
            </Box>{" "}
            {notes}
          </Typography>
        </Box>
      )}

      {(depositValue || isDepositHolder || depositLockedElsewhere || isLockedByJob) && (
        <Box
          sx={{
            mt: 1.25,
            pt: 1.15,
            borderTop: `1px solid ${quoteModern.colors.line}`,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.75,
          }}
        >
          {depositValue && (
            <Chip
              label={`Deposit: ${depositValue}`}
              size="small"
              variant="outlined"
              sx={{
                height: 22,
                fontSize: 10.5,
                fontWeight: 800,
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                borderColor: quoteModern.colors.line,
                color: quoteModern.colors.textPrimary,
              }}
            />
          )}

          {isDepositHolder && (
            <Chip
              label="Deposit applied"
              size="small"
              variant="outlined"
              sx={{
                height: 22,
                fontSize: 10.5,
                fontWeight: 800,
                borderColor: colors.success[600],
                bgcolor: alphaVar(colors.success[100], 0.35),
                color: colors.success[800],
              }}
            />
          )}

          {depositLockedElsewhere && (
            <Chip
              label="Deposit reserved elsewhere"
              size="small"
              variant="outlined"
              sx={{
                height: 22,
                fontSize: 10.5,
                fontWeight: 800,
                borderColor: colors.warning[600],
                bgcolor: alphaVar(colors.warning[100], 0.35),
                color: colors.warning[800],
              }}
            />
          )}

          {isLockedByJob && (
            <Chip
              label="Already linked to a job"
              size="small"
              variant="outlined"
              sx={{
                height: 22,
                fontSize: 10.5,
                fontWeight: 800,
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                borderColor: quoteModern.colors.line,
                color: quoteModern.colors.textSecondary,
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );

  if (isLockedByJob) {
    return (
      <Tooltip title={`Job #${lockingJob.id} is already linked to this quote service.`} placement="top" arrow>
        <span style={{ display: "block" }}>{card}</span>
      </Tooltip>
    );
  }

  return card;
}

function ServiceCard(props) {
  const quoteType = clean(props?.quoteType).toLowerCase();

  if (quoteType === "basic") {
    return <BasicServiceCard {...props} />;
  }

  return <AdvancedServiceCard {...props} />;
}

function DesktopRowCells({ quote, lockDepositAddActions = false }) {
  return (
    <>
      <TableCell
        sx={{
          padding: "8px 4px",
          verticalAlign: "middle",
          whiteSpace: "normal",
        }}
      >
        <Typography
          sx={{
            maxWidth: 150,
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: "text.primary",
            lineHeight: 1.25,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            whiteSpace: "normal",
            overflowWrap: "break-word",
            wordBreak: "normal",
          }}
        >
          {getClientName(quote.client)}
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: "8px 4px" }}>
        <Typography
          fontSize="0.7rem"
          color="text.secondary"
          sx={{
            maxWidth: 160,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={quote.alias || ""}
        >
          {quote.alias || "—"}
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: "8px 4px" }}>
        <Typography
          fontSize="0.7rem"
          color="text.secondary"
          sx={{
            maxWidth: 180,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {getLocation(quote.coverageArea)}
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: "8px 4px" }} align="center">
        <DepositCell quote={quote} lockDepositAddActions={lockDepositAddActions} />
      </TableCell>

      <TableCell sx={{ padding: "8px 4px" }} align="right">
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 700,
            color: quoteModern.colors.orange,
            lineHeight: 1.25,
            whiteSpace: "nowrap",
          }}
        >
          ${quote.__computed?.totalCost?.toLocaleString() ?? "—"}
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: "8px 4px" }}>
        <StatusSelect quoteId={quote.id} status={quote.status} />
      </TableCell>

      <TableCell sx={{ padding: "8px 4px" }}>
        <Typography fontSize="0.75rem" color="text.secondary">
          {dayjs(quote.createdAt).format("MMM D")}
        </Typography>
      </TableCell>
    </>
  );
}

export function QuoteRow({ quote, enableSelection = false, isMobile = false, lockDepositAddActions = false }) {
  const [open, setOpen] = useState(false);

  const dispatch = useDispatch();

  const selectedService = useSelector((state) => state.jobCreateStepper.selectedService);

  const detailsQueryMobile = useMediaQuery("(max-width:600px)");

  const isOnMobile = isMobile || detailsQueryMobile;

  const depositRowQuery = useQuoteDepositAvailable(enableSelection && open ? quote?.id : undefined);

  const lockingJobByQuoteServiceUid = useMemo(() => buildLockingJobByQuoteServiceUid(quote?.job), [quote?.job]);

  const handleSelectService = (service) => {
    if (!enableSelection) return;

    if (lockingJobByQuoteServiceUid.get(service.serviceUid)) {
      return;
    }

    dispatch(
      selectService({
        quoteId: quote.id,
        quoteType: quote.type,
        service,
      }),
    );

    dispatch(setQuote(quote));
  };

  const isServiceSelected = (serviceUid) => {
    return selectedService?.service?.serviceUid === serviceUid;
  };

  if (!enableSelection && isOnMobile) {
    return (
      <BaseModalComponent
        modalLabel={`Quote #${quote?.id}`}
        modalKey={`QuoteRow-ViewQuote-${quote?.id}`}
        defaultSizes={{}}
        elementTrigger={
          <TableRow
            hover
            sx={{
              cursor: "pointer",

              "&:hover": {
                backgroundColor: alphaVar(quoteModern.colors.orange, 0.05),
              },
            }}
          >
            <TableCell sx={{ padding: "8px 4px" }}>
              <Box />
            </TableCell>

            <TableCell sx={{ padding: "8px 4px" }}>
              <Typography fontSize="0.75rem" fontWeight={600}>
                #{quote.id}
              </Typography>
            </TableCell>

            <TableCell sx={{ padding: "8px 4px" }}>
              <Typography
                sx={{
                  maxWidth: 150,
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  color: "text.primary",
                  lineHeight: 1.25,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {getClientName(quote.client)}
              </Typography>
            </TableCell>

            <TableCell sx={{ padding: "8px 4px" }}>
              <StatusSelect quoteId={quote.id} status={quote.status} />
            </TableCell>

            <TableCell sx={{ padding: "8px 4px" }}>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  borderColor: alphaVar(quoteModern.colors.orange, 0.3),
                  color: quoteModern.colors.orange,
                  textTransform: "none",
                  fontSize: "0.7rem",

                  "&:hover": {
                    backgroundColor: alphaVar(quoteModern.colors.orange, 0.05),
                    borderColor: quoteModern.colors.orange,
                  },
                }}
              >
                View
              </Button>
            </TableCell>
          </TableRow>
        }
      >
        <QuoteView quoteId={quote?.id} />
      </BaseModalComponent>
    );
  }

  const isArchived = Boolean(quote.archivedAt);

  const rowSx = {
    ...(isArchived
      ? {
          opacity: 0.55,
        }
      : {}),

    ...(!enableSelection
      ? {
          cursor: "pointer",

          "&:hover": {
            backgroundColor: alphaVar(quoteModern.colors.orange, 0.05),
          },
        }
      : {}),
  };

  const rowContent = (firstCell) => (
    <TableRow sx={rowSx}>
      <TableCell sx={{ padding: "8px 4px" }}>{firstCell}</TableCell>

      <TableCell sx={{ padding: "8px 4px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography fontSize="0.75rem">#{quote.id}</Typography>

          {isArchived && (
            <Chip
              label="Archived"
              size="small"
              sx={{
                height: 16,
                fontSize: "0.6rem",
                bgcolor: colors.gray[200],
                color: colors.gray[700],
              }}
            />
          )}
        </Box>
      </TableCell>

      {!isMobile && <DesktopRowCells quote={quote} lockDepositAddActions={lockDepositAddActions} />}

      <TableCell
        sx={{
          padding: "8px 4px",
          width: 48,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <QuoteRowActionsMenu quote={quote} />
      </TableCell>
    </TableRow>
  );

  const mainRow = enableSelection ? (
    rowContent(
      <IconButton size="small" onClick={() => setOpen((current) => !current)}>
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>,
    )
  ) : (
    <BaseModalComponent
      modalLabel={`Quote #${quote?.id}`}
      modalKey={`QuoteRow-ViewQuote-${quote?.id}-${dayjs(quote.createdAt)}`}
      defaultSizes={{
        width: {
          xs: "95%",
          sm: "90%",
          md: "85%",
          lg: "1200px",
        },
        height: {
          xs: "90%",
          sm: "85%",
          md: "85%",
          lg: "90%",
        },
      }}
      elementTrigger={rowContent(<Box />)}
    >
      <QuoteView quoteId={quote?.id} />
    </BaseModalComponent>
  );

  return (
    <>
      {mainRow}

      {enableSelection && (
        <TableRow>
          <TableCell
            colSpan={isMobile ? 3 : 10}
            sx={{
              p: 0,
              borderBottom: "none",
            }}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  p: {
                    xs: 1.25,
                    sm: 1.5,
                  },
                  borderTop: `1px solid ${quoteModern.colors.line}`,
                  backgroundColor: quoteModern.colors.sheetBg,
                }}
              >
                <Typography
                  sx={{
                    ...quoteModern.labelSx,
                    mb: 1.5,
                    color: quoteModern.colors.textPrimary,
                  }}
                >
                  Services
                </Typography>

                <Box
                  sx={{
                    maxHeight: "none",
                    overflow: "visible",
                    pr: 0.5,
                  }}
                >
                  <Stack spacing={1.25}>
                    {(quote.services ?? []).map((service, index) => (
                      <ServiceCard
                        key={service.serviceUid ?? service.uid ?? index}
                        service={service}
                        quoteType={quote.type}
                        isSelected={isServiceSelected(service.serviceUid)}
                        onSelect={() => handleSelectService(service)}
                        depositReservedQuoteServiceUid={depositRowQuery.data?.depositReservedQuoteServiceUid}
                        depositAvailable={depositRowQuery.data?.depositAvailable}
                        depositRecognizedPaid={depositRowQuery.data?.depositRecognizedPaid}
                        quoteDepositAmount={quote.depositAmount}
                        lockingJob={lockingJobByQuoteServiceUid.get(service.serviceUid)}
                      />
                    ))}
                  </Stack>
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
